import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { BsShare, BsPostcardHeart, BsPencilSquare, BsEnvelope } from 'react-icons/bs';
import * as Yup from 'yup';

import BayAddButton from '../../BayAddButton';
import BayLoader from '../../BayLoader';
import BayModal from '../../BayModal';
import { presets } from '../../BayPolotno/sections/PageLayouts/presets';
import { customPreset } from '../../BayPolotno/sections/PageLayouts/presets/presetDefinitions';
import BaySelect from '../../BaySelect';
import * as Presets from '../Presets';
import './CreateModal.scss';

interface FilterItem {
	id: string;
	viewname: string;
	active: boolean;
}

interface Preset {
	id: string;
	category: string;
	platform?: string;
	label: string;
	width: number;
	height: number;
	presetComponent: string;
	[key: string]: any;
}

interface UnitOption {
	label: string;
	value: string;
}

interface CustomFormValues {
	width: number;
	height: number;
	unit: UnitOption;
}

interface InitialView {
	productType?: 'social' | 'print' | 'email' | 'custom';
	platform?: string;
	mediaType?: 'image' | 'video' | null;
}

interface CreateModalProps {
	show: boolean;
	onHide: () => void;
	title?: string;
	t?: (key: string) => string;
	handleCreate?: (preset: any) => void;
	presetVisibility?: Record<string, boolean>;
	emailEnabled?: boolean;
	disabledPresets?: Record<string, boolean> | null;
	initialView?: InitialView | null;
}

const PX_PER_INCH = 96;

// Converts a width/height value between the custom form's unit options. Pixels round to whole
// numbers (no sub-pixel dimensions); inches keep 2 decimal places for a legible round trip.
const convertDimension = (value, fromUnit, toUnit) => {
	const numericValue = parseFloat(value);
	if (fromUnit === toUnit || Number.isNaN(numericValue)) {
		return value;
	}
	if (fromUnit === 'px' && toUnit === 'in') {
		return Number((numericValue / PX_PER_INCH).toFixed(2));
	}
	if (fromUnit === 'in' && toUnit === 'px') {
		return Math.round(numericValue * PX_PER_INCH);
	}
	return value;
};

// Yup validation schema for custom form
const customFormSchema = Yup.object().shape({
	width: Yup.number()
		.required('Width is required')
		.positive('Width must be greater than 0')
		.test('max-value', function (value) {
			const { unit } = this.parent;
			const maxValue =
				unit?.value === 'px' ? customPreset.maxPx : customPreset.maxInches;
			const unitLabel = unit?.value === 'px' ? 'px' : 'in';

			if (value && value > maxValue) {
				return this.createError({
					message: `Width can't exceed ${maxValue} ${unitLabel}`,
				});
			}
			return true;
		}),
	height: Yup.number()
		.required('Height is required')
		.positive('Height must be greater than 0')
		.test('max-value', function (value) {
			const { unit } = this.parent;
			const maxValue =
				unit?.value === 'px' ? customPreset.maxPx : customPreset.maxInches;
			const unitLabel = unit?.value === 'px' ? 'px' : 'in';

			if (value && value > maxValue) {
				return this.createError({
					message: `Height can't exceed ${maxValue} ${unitLabel}`,
				});
			}
			return true;
		}),
	unit: Yup.object()
		.shape({
			label: Yup.string().required(),
			value: Yup.string()
				.required('Unit is required.')
				.oneOf(customPreset.availableUnits),
		})
		.required('A unit must be selected.'),
});

const SOCIAL_FILTERS = [
	{ id: 'all', viewname: 'All' },
	{ id: 'instagram', viewname: 'Instagram' },
	{ id: 'facebook', viewname: 'Facebook' },
	{ id: 'linkedin', viewname: 'LinkedIn' },
	{ id: 'x', viewname: 'X' },
];

const PRINT_FILTERS = [
	{ id: 'all', viewname: 'All' },
	{ id: 'postcard', viewname: 'Postcards' },
	{ id: 'flyer', viewname: 'Flyers' },
];

// Image/video segmented toggle for social posts (Figma node 29263-2551).
const MEDIA_TYPE_FILTERS: { id: string; label: string; value: string | null }[] = [
	{ id: 'all', label: 'All', value: null },
	{ id: 'image', label: 'Image', value: 'image' },
	{ id: 'video', label: 'Video', value: 'video' },
];

// Returns the filter list with the entry matching `activeId` marked active
// (falling back to "all" when no id is supplied).
const withActiveFilter = (
	filters: { id: string; viewname: string }[],
	activeId?: string | null,
): FilterItem[] => {
	const resolvedId = filters.some((filter) => filter.id === activeId)
		? activeId
		: 'all';

	return filters.map((filter) => ({
		...filter,
		active: filter.id === resolvedId,
	}));
};

const CreateModal: React.FC<CreateModalProps> = ({
	show,
	onHide,
	title = 'Create Template',
	t = (key) => key,
	handleCreate = () => {},
	presetVisibility = {},
	emailEnabled = false,
	disabledPresets = {},
	initialView = null,
}) => {
	const initialProductType: 'social' | 'print' | 'email' | 'custom' =
		initialView?.productType === 'email' && !emailEnabled
			? 'social'
			: initialView?.productType || 'social';
	const [activeProductType, setActiveProductType] =
		useState<'social' | 'print' | 'email' | 'custom'>(initialProductType);
	// Optional image/video narrowing for social posts (overview quick-launch tiles).
	const [mediaTypeFilter, setMediaTypeFilter] = useState<string | null>(
		initialProductType === 'social' ? initialView?.mediaType || null : null,
	);
	const formikRef = useRef<FormikProps<CustomFormValues>>(null);
	const [socialFilters, setSocialFilters] = useState<FilterItem[]>(() =>
		withActiveFilter(
			SOCIAL_FILTERS,
			initialView?.productType === 'social' ? initialView?.platform : null,
		),
	);

	const [printFilters, setPrintFilters] = useState<FilterItem[]>(() =>
		withActiveFilter(
			PRINT_FILTERS,
			initialView?.productType === 'print' ? initialView?.platform : null,
		),
	);

	const [emailFilters, setEmailFilters] = useState<FilterItem[]>([
		{
			id: 'all',
			viewname: 'All',
			active: true,
		},
	]);

	// quick-launch tile can land on a stale platform/media type.
	useEffect(() => {
		if (!show) return;
		setActiveProductType(initialProductType);
		setMediaTypeFilter(
			initialProductType === 'social' ? initialView?.mediaType || null : null,
		);
		setSocialFilters(
			withActiveFilter(
				SOCIAL_FILTERS,
				initialView?.productType === 'social' ? initialView?.platform : null,
			),
		);
		setPrintFilters(
			withActiveFilter(
				PRINT_FILTERS,
				initialView?.productType === 'print' ? initialView?.platform : null,
			),
		);
		// Keyed on initialView's fields (not its identity) so a parent passing a
		// fresh object each render can't reset filters mid-interaction.
	}, [
		show,
		initialProductType,
		initialView?.productType,
		initialView?.platform,
		initialView?.mediaType,
	]);

	// Marks the clicked pill active (others inactive) for the current product
	// type. Rendered inline as pills to match Figma (node 29263-2542) — no
	// "More" overflow dropdown.
	const handleFilterClick = (filterId: string) => {
		const updateFilters = (filters: FilterItem[]) =>
			filters.map((filter) => ({
				...filter,
				active: filter.id === filterId,
			}));

		if (activeProductType === 'social') {
			setSocialFilters(updateFilters);
		} else if (activeProductType === 'print') {
			setPrintFilters(updateFilters);
		}
	};

	const handleProductTypeChange = (newProductType: typeof activeProductType) => {
		setActiveProductType(newProductType);
		// Image/video narrowing only applies to social; clear it when switching.
		setMediaTypeFilter(null);

		// Reset the form when the product type changes
		if (formikRef.current) {
			formikRef.current.resetForm();
		}

		// Reset filters to "All" when switching product types
		if (newProductType === 'social') {
			setSocialFilters((prev) =>
				prev.map((filter) => ({
					...filter,
					active: filter.id === 'all',
				})),
			);
		} else if (newProductType === 'print') {
			setPrintFilters((prev) =>
				prev.map((filter) => ({
					...filter,
					active: filter.id === 'all',
				})),
			);
		} else if (newProductType === 'email') {
			setEmailFilters((prev) =>
				prev.map((filter) => ({
					...filter,
					active: filter.id === 'all',
				})),
			);
		}
	};

	const filteredPresets = useMemo(
		() =>
			disabledPresets === null
				? []
				: presets.filter((p: Preset) => disabledPresets[p.id] !== false),
		[disabledPresets],
	);

	const socialPresets = filteredPresets.filter((p: Preset) => p.category === 'social');
	const printPresets = filteredPresets.filter((p: Preset) => p.category === 'print');
	const emailPresets = filteredPresets.filter((p: Preset) => p.category === 'email');

	const activeFilters = activeProductType === 'social' ? socialFilters :
		activeProductType === 'print' ? printFilters : emailFilters;
	const activeFilterId = activeFilters.find((f) => f.active)?.id;

	const presetsToShow = (
		activeProductType === 'social' ? socialPresets :
			activeProductType === 'print' ? printPresets : emailPresets
	).filter((p: Preset) => {
		// Filter by platform/type
		const platformMatch = activeFilterId === 'all' || p.platform === activeFilterId;

		// Filter by visibility (default to visible if not specified)
		const isVisible = presetVisibility[p.id] !== false;

		// Optional image/video narrowing (overview quick-launch tiles)
		const mediaMatch =
			activeProductType !== 'social' || !mediaTypeFilter || p.type === mediaTypeFilter;

		return platformMatch && isVisible && mediaMatch;
	});

	// Helper function to convert pixels to inches at 300 DPI (standard for print)
	const pixelsToInches = (pixels: number): string => {
		return (pixels / 300).toFixed(1);
	};

	// Helper function to format preset dimensions based on category
	const formatPresetDimensions = (preset: Preset): string => {
		if (preset.category === 'print') {
			const widthInches = pixelsToInches(preset.width);
			const heightInches = pixelsToInches(preset.height);
			return `${widthInches} × ${heightInches} in`;
		} else {
			return `${preset.width} × ${preset.height} px`;
		}
	};

	return (
		<BayModal
			centered
			hideFooter
			noPadding
			className={clsx('create-modal')}
			closeButton
			height="80vh"
			show={show}
			size="xl"
			title={title}
			translate={t}
			onHideFunc={onHide}
		>
			<div className="create-modal-wrapper d-flex h-100 border-top">
				<div className="create-modal-sidemenu p-3 border-end flex-shrink-0">
					<button
						className={clsx('sidemenu-button d-flex align-items-center w-100', {
							active: activeProductType === 'social',
						})}
						onClick={() => handleProductTypeChange('social')}
					>
						<BsShare className="sidemenu-icon me-2" />
						Social Posts
					</button>
					<button
						className={clsx('sidemenu-button d-flex align-items-center w-100', {
							active: activeProductType === 'print',
						})}
						onClick={() => handleProductTypeChange('print')}
					>
						<BsPostcardHeart className="sidemenu-icon me-2" />
						Print Products
					</button>
					{emailEnabled && (
						<button
							className={clsx('sidemenu-button d-flex align-items-center w-100', {
								active: activeProductType === 'email',
							})}
							onClick={() => handleProductTypeChange('email')}
						>
							<BsEnvelope className="sidemenu-icon me-2" />
							Email
						</button>
					)}
					<hr />
					<button
						className={clsx('sidemenu-button d-flex align-items-center w-100', {
							active: activeProductType === 'custom',
						})}
						onClick={() => handleProductTypeChange('custom')}
					>
						<BsPencilSquare className="sidemenu-icon me-2" />
						Custom
					</button>
				</div>
				<div className="create-modal-content flex-grow-1 d-flex flex-column overflow-auto">
					{activeProductType === 'custom' ? (
						<div className="p-4">
							<div className="mb-4">
								<h3>Create a Custom Sized Project from Scratch</h3>
								<p className="text-muted">
									Got something unique in mind? Enter your custom dimensions
									below and bring your vision to life.
								</p>
							</div>

							<Formik<CustomFormValues>
								validateOnMount
								initialValues={{
									width: customPreset.width,
									height: customPreset.height,
									unit: { label: 'px', value: 'px' },
								}}
								innerRef={formikRef}
								validationSchema={customFormSchema}
								onSubmit={(values, { setSubmitting }) => {
									const customProject = {
										...customPreset,
										width: values.width,
										height: values.height,
										unit: values.unit.value,
									};
									handleCreate(customProject);
									setSubmitting(false);
								}}
							>
								{({
									values,
									errors,
									touched,
									handleChange,
									handleBlur,
									handleSubmit,
									isSubmitting,
									setFieldValue,
									setFieldTouched,
									setValues,
									setTouched,
									isValid,
								}) => {
									const unitOptions: UnitOption[] = customPreset.availableUnits.map(
										(unit: string) => ({
											label: unit,
											value: unit,
										}),
									);

									const handleUnitChange = (option) => {
										const prevUnit = values.unit?.value;
										const nextUnit = option?.value;

										if (!prevUnit || !nextUnit || prevUnit === nextUnit) {
											setFieldValue('unit', option);
											setFieldTouched('unit', true);
											return;
										}

										setValues({
											...values,
											width: convertDimension(values.width, prevUnit, nextUnit),
											height: convertDimension(values.height, prevUnit, nextUnit),
											unit: option,
										});
										setTouched(
											{
												...touched,
												width: true,
												height: true,
												unit: { label: true, value: true },
											},
											false,
										);
									};

									return (
										<Form
											noValidate
											id="custom_template_form"
											onSubmit={handleSubmit}
										>
											<Row className="mb-4">
												<Col md={4}>
													<Form.Group>
														<Form.Label className="text-dark fw-bold">
															Width <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															isInvalid={touched.width && !!errors.width}
															min="1"
															name="width"
															step="0.1"
															type="number"
															value={values.width}
															onBlur={handleBlur}
															onChange={handleChange}
														/>
														<Form.Control.Feedback type="invalid">
															{errors.width}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>

												<Col md={4}>
													<Form.Group>
														<Form.Label className="text-dark fw-bold">
															Height <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															isInvalid={touched.height && !!errors.height}
															min="1"
															name="height"
															step="0.1"
															type="number"
															value={values.height}
															onBlur={handleBlur}
															onChange={handleChange}
														/>
														<Form.Control.Feedback type="invalid">
															{errors.height}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>

												<Col md={4}>
													<Form.Group>
														<Form.Label className="text-dark fw-bold">
															Units <span className="text-danger">*</span>
														</Form.Label>
														<BaySelect
															controlledValue={values.unit}
															isClearable={false}
															menuPortalTarget={document.body}
															optionsArray={unitOptions}
															styles={{
																menuPortal: (base) => ({
																	...base,
																	// eslint-disable-next-line no-restricted-syntax -- grandfathered: raw z-index fenced as step 2b, the portal band is a cross-repo decision. See docs/css-unification/overlay-and-zindex.md
																	zIndex: 9999,
																}),
															}}
															onBlur={() => {
																setFieldTouched('unit', true);
															}}
															onChangeFunc={handleUnitChange}
														/>
														<Form.Control.Feedback type="invalid">
															{typeof errors.unit === 'string' ? errors.unit : (errors.unit as any)?.value}
														</Form.Control.Feedback>
													</Form.Group>
												</Col>
											</Row>

											<div className="d-flex">
												<BayAddButton
													disabled={isSubmitting || !isValid}
													text="Create Project"

													onClick={handleSubmit as any}
												/>
											</div>
										</Form>
									);
								}}
							</Formik>
						</div>
					) : disabledPresets === null ? (
						<div className="content-body d-flex align-items-center justify-content-center flex-grow-1 overflow-y-auto pe-2">
							<div className="d-flex flex-column align-items-center py-5">
								<BayLoader small />
								<p className="text-muted mt-3 mb-0">Loading presets...</p>
							</div>
						</div>
					) : (activeProductType === 'email' && emailEnabled) ? (
						<div className="d-flex flex-column h-100 align-items-center justify-content-start text-center px-5">
							<div className="my-4">
								<h3 className="mb-3">Design Your Own Email Template</h3>
								<p className="text-muted mb-4">
									Whether it&apos;s a welcome email, newsletter, or announcement - this is your blank canvas.<br/>
									Let&apos;s get creative.
								</p>
							</div>
							<BayAddButton
								className="mb-4"
								disabled={!emailPresets.length}
								text="Create Template"
								onClick={() => handleCreate(emailPresets[0])}
							/>
							<div className="d-flex justify-content-center">
								<img
									alt="Email template illustration"
									src="https://bay.cdn.backatyou.com/ui/placeholders/dsa-placeholder.svg"
									style={{ maxWidth: '250px', height: 'auto' }}
								/>
							</div>
						</div>
					) : (
						<>
							<div className="create-modal-filters position-relative d-flex align-items-center gap-3 flex-shrink-0 mb-3">
								<div
									className="filter-pills d-flex flex-wrap align-items-center gap-2 flex-fill"
									role="group"
									aria-label="Filter templates by platform"
								>
									{activeFilters.map((filter) => (
										<button
											key={filter.id}
											type="button"
											aria-pressed={filter.active}
											className={clsx('filter-pill d-inline-flex align-items-center justify-content-center px-3 py-1 border rounded-pill lh-sm text-nowrap', {
												active: filter.active,
											})}
											onClick={() => handleFilterClick(filter.id)}
										>
											{filter.viewname}
										</button>
									))}
								</div>
								{activeProductType === 'social' && (
									<div
										className="media-type-toggle position-static d-inline-flex align-items-center flex-shrink-0 gap-1 p-1 mb-0 rounded-pill"
										role="group"
										aria-label="Filter social posts by media type"
									>
										{MEDIA_TYPE_FILTERS.map((option) => (
											<button
												key={option.id}
												type="button"
												aria-pressed={mediaTypeFilter === option.value}
												className={clsx('media-type-toggle-btn px-3 py-2 border-0 rounded-pill lh-base text-nowrap cursor-pointer', {
													active: mediaTypeFilter === option.value,
												})}
												onClick={() => setMediaTypeFilter(option.value)}
											>
												{option.label}
											</button>
										))}
									</div>
								)}
								<div className="create-modal-filters-divider position-absolute start-0 end-0 bottom-0" />
							</div>
							<div className="content-body flex-grow-1 overflow-y-auto pe-2">
								<div className={clsx('presets-grid', { 'pt-0': activeProductType === 'print' })}>
									{presetsToShow.map((preset: Preset) => {
										const PresetComponent = (Presets as any)[preset.presetComponent];
										return (
											<div
												key={preset.id}
												className="preset-card d-flex flex-column align-items-center text-center border-0"
												role="button"
												tabIndex={0}
												onClick={() => handleCreate(preset)}
											>
												<div className="preset-image d-flex flex-column align-items-center justify-content-end mb-3">
													{PresetComponent && <PresetComponent />}
												</div>
												<div className="preset-name fs-6 fw-bold mb-1">{preset.label}</div>
												<div className="preset-dimensions fs-7 text-500">
													{formatPresetDimensions(preset)}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</BayModal>
	);
};

export default CreateModal;
